import React, { useState } from 'react';

type DonationFrequency = 'MONTHLY' | 'ANNUALLY' | 'ONETIME';

interface LevelOption {
  title: string;
  monthly: number;
  annually: number;
  onetime: number;
  description: string;
  isOneTimeOnly?: boolean;
}

export interface SelectedLevel {
  title: string;
  amount: number;
  frequency: DonationFrequency;
  description: string;
}

export const LEVELS: LevelOption[] = [
  { title: 'Heritage Partner', monthly: 0, annually: 0, onetime: 12, description: 'The modern equivalent of $12 per year in 1955. A meaningful gift to sustain the foundation.', isOneTimeOnly: true },
  { title: 'Community Builder', monthly: 12, annually: 144, onetime: 144, description: 'The modern equivalent of $1 per month in 1955. Sustains the Family Foundation.' },
  { title: 'Sustainer', monthly: 25, annually: 300, onetime: 300, description: 'Strengthens volunteer services and provides dependable year-round support.' },
  { title: 'Champion', monthly: 50, annually: 600, onetime: 600, description: 'Makes a measurable impact and accelerates progress.' },
  { title: 'Legacy Leader', monthly: 120, annually: 1440, onetime: 1440, description: "Honoring Old Doc Brooke's $10/month leadership gift in 1955 — the modern equivalent of bold commitment." },
];

const FREQUENCY_LABELS: Record<DonationFrequency, string> = {
  MONTHLY: 'Monthly',
  ANNUALLY: 'Annually',
  ONETIME: 'One-Time',
};

interface DonationAmountSelectorProps {
  onLevelSelect: (level: SelectedLevel) => void;
}

const DonationAmountSelector: React.FC<DonationAmountSelectorProps> = ({ onLevelSelect }) => {
  const [activeTab, setActiveTab] = useState<DonationFrequency>('MONTHLY');

  const getAmount = (level: LevelOption): number => {
    if (level.isOneTimeOnly) return level.onetime;
    return activeTab === 'MONTHLY' ? level.monthly : activeTab === 'ANNUALLY' ? level.annually : level.onetime;
  };

  const formatAmount = (amount: number) =>
    amount >= 1000 ? `$${amount.toLocaleString()}` : `$${amount}`;

  const handleCardClick = (level: LevelOption) => {
    const freq = level.isOneTimeOnly ? 'ONETIME' : activeTab;
    onLevelSelect({ title: level.title, amount: getAmount(level), frequency: freq, description: level.description });
  };

  // Filter: Heritage Partner only shows in One-Time tab
  const visibleLevels = LEVELS.filter(level => {
    if (level.isOneTimeOnly && activeTab !== 'ONETIME') return false;
    return true;
  });

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 border border-gray-100 w-full">
      {/* Section Title */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-8 h-8 bg-[#71001F] rounded-full flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-gray-800">Choose Your Level of Impact</h3>
      </div>

      {/* Frequency Tabs — prominent at top */}
      <div className="flex gap-2 mb-6 bg-gray-100 p-1.5 rounded-lg">
        {(Object.keys(FREQUENCY_LABELS) as DonationFrequency[]).map((freq) => (
          <button
            key={freq}
            onClick={() => setActiveTab(freq)}
            className={`flex-1 px-4 py-2.5 rounded-md text-sm font-semibold transition-all duration-200 ${
              activeTab === freq
                ? 'bg-[#71001F] text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            {FREQUENCY_LABELS[freq]}
          </button>
        ))}
      </div>

      {/* Level Cards — 2 column grid matching Figma */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {visibleLevels.map((level) => (
          <button
            key={level.title}
            onClick={() => handleCardClick(level)}
            className="relative text-left p-5 rounded-xl border-2 border-gray-200 hover:border-[#71001F] hover:bg-red-50 transition-all duration-200 group"
          >
            <div className="text-xs font-bold uppercase tracking-wide text-gray-400 group-hover:text-[#71001F] mb-1">
              {level.title}
            </div>
            <div className="flex items-baseline gap-1 mb-2">
              <span className="text-2xl font-bold text-gray-900 group-hover:text-[#71001F]">
                {formatAmount(getAmount(level))}
              </span>
              {!level.isOneTimeOnly && activeTab !== 'ONETIME' && (
                <span className="text-sm text-gray-500">
                  {activeTab === 'MONTHLY' ? '/month' : '/year'}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">{level.description}</p>
          </button>
        ))}
      </div>
    </div>
  );
};

export default DonationAmountSelector;
